import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.getClientRects().length > 0,
  )

type FocusTrapOptions = {
  onClose?: () => void
  /** Куда вернуть фокус при закрытии; по умолчанию — элемент, бывший в фокусе при открытии. */
  triggerRef?: RefObject<HTMLElement | null>
}

/**
 * Захват фокуса для модального диалога: Tab/Shift+Tab ходят по кругу внутри контейнера,
 * Escape закрывает, при открытии фокус уходит на первый элемент, при закрытии возвращается.
 */
export const useFocusTrap = <T extends HTMLElement = HTMLElement>(
  enabled: boolean,
  options: FocusTrapOptions = {},
): RefObject<T> => {
  const containerRef = useRef<T>(null)
  const optionsRef = useRef(options)

  useEffect(() => {
    optionsRef.current = options
  })

  useEffect(() => {
    if (!enabled) return

    const container = containerRef.current
    if (!container) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    getFocusableElements(container)[0]?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const { onClose } = optionsRef.current
        if (!onClose) return
        event.preventDefault()
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return

      const focusables = getFocusableElements(container)
      if (focusables.length === 0) {
        event.preventDefault()
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || !container.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || !container.contains(active))) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      const target = optionsRef.current.triggerRef?.current ?? previouslyFocused
      if (target && document.contains(target)) target.focus()
    }
  }, [enabled])

  return containerRef
}
